/**
 * Workspaces (one per business), their members and their invitations.
 *
 * Access is decided by the `memberships` rows: owners and members may write, accountants and
 * viewers may only read and export. The same rows drive the PocketBase API rules, so the checks
 * here are for the UI, not for security.
 */
import type {
  MembershipsResponse,
  UsersResponse,
  InvitesResponse,
  WorkspacesResponse,
} from "@/lib/platform/pb-types";
import type { Workplace } from "@/lib/at/e1a";
import type { ExportSettings } from "@/lib/export/settings";
import { pb } from "@/lib/platform/pocketbase";

export type Workspace = WorkspacesResponse;
export type Membership = MembershipsResponse<{ user?: UsersResponse }>;
export type Invite = InvitesResponse;
export type MemberRole = Membership["role"];

/** Roles that may change data. Mirrors `canWrite` in server/pb_hooks/bookkeeping.pb.js. */
const WRITE_ROLES: MemberRole[] = ["owner", "member"];

export type WorkspaceInput = {
  name: string;
  legalName: string;
  legalForm: Workspace["legalForm"];
  vatId: string;
  taxNumber: string;
  street: string;
  zip: string;
  city: string;
  country: string;
  email: string;
  iban: string;
  accountingBasis: Workspace["accountingBasis"];
  vatScheme: Workspace["vatScheme"];
  vatPeriod: Workspace["vatPeriod"];
  smallBusinessLimit: number;
  fiscalYearStartMonth: number;
  currency: string;
  /** Settings → Accounting export; see `lib/export/settings.ts`. */
  accountingExport?: ExportSettings;
  /** The E1a draft's choices per year (`lib/at/e1a.ts`). */
  taxYears?: TaxYears;
};

export type TaxYear = { workplace: Workplace; reducedFlatRate: boolean };
export type TaxYears = Record<string, TaxYear>;

/** A year's choices, with the defaults for a year nobody has looked at yet. */
export function taxYearOf(workspace: Pick<Workspace, "taxYears"> | null | undefined, year: number) {
  const stored = (workspace?.taxYears as TaxYears | null | undefined)?.[String(year)];
  return {
    workplace: stored?.workplace ?? "none",
    reducedFlatRate: stored?.reducedFlatRate ?? false,
  } satisfies TaxYear;
}

/** What a new Austrian one-person business most often needs. */
export const WORKSPACE_DEFAULTS: WorkspaceInput = {
  name: "",
  legalName: "",
  legalForm: "sole_proprietor",
  vatId: "",
  taxNumber: "",
  street: "",
  zip: "",
  city: "",
  country: "AT",
  email: "",
  iban: "",
  accountingBasis: "cash",
  vatScheme: "standard",
  vatPeriod: "quarterly",
  smallBusinessLimit: 55000,
  fiscalYearStartMonth: 1,
  currency: "EUR",
};

export async function listWorkspaces(): Promise<Workspace[]> {
  return pb.collection("workspaces").getFullList<Workspace>({ sort: "name" });
}

/**
 * The person's own order (`users.workspaceOrder`, ids first to last), then everything it does not
 * name - a new business, an accepted invitation - in the order given (alphabetical).
 */
export function orderWorkspaces(workspaces: Workspace[], order: unknown): Workspace[] {
  const ids = Array.isArray(order) ? order.filter((id) => typeof id === "string") : [];
  const position = (workspace: Workspace) => {
    const index = ids.indexOf(workspace.id);
    return index === -1 ? ids.length : index;
  };
  // Array.sort is stable: the unnamed ones keep their alphabetical order.
  return [...workspaces].sort((a, b) => position(a) - position(b));
}

/** Saves the order on the person's own record; the auth store picks up the new record. */
export async function saveWorkspaceOrder(userId: string, ids: string[]): Promise<void> {
  await pb.collection("users").update(userId, { workspaceOrder: ids });
}

export async function createWorkspace(input: WorkspaceInput, ownerId: string): Promise<Workspace> {
  // The hook adds the owner's membership and the default chart of accounts.
  return pb.collection("workspaces").create({ ...input, owner: ownerId });
}

export async function updateWorkspace(
  id: string,
  input: Partial<WorkspaceInput>,
): Promise<Workspace> {
  return pb.collection("workspaces").update(id, input);
}

export async function archiveWorkspace(id: string): Promise<Workspace> {
  return pb.collection("workspaces").update(id, { archived: true });
}

/** Every membership of the signed-in user: which workspaces they may open and with what rights. */
export async function listMyMemberships(userId: string): Promise<Membership[]> {
  return pb
    .collection("memberships")
    .getFullList<Membership>({ filter: pb.filter("user = {:userId}", { userId }) });
}

export async function listMembers(workspaceId: string): Promise<Membership[]> {
  return pb.collection("memberships").getFullList<Membership>({
    filter: pb.filter("workspace = {:workspaceId}", { workspaceId }),
    sort: "created",
    expand: "user",
  });
}

export async function updateMemberRole(id: string, role: MemberRole): Promise<Membership> {
  return pb.collection("memberships").update(id, { role });
}

export async function removeMember(id: string): Promise<void> {
  await pb.collection("memberships").delete(id);
}

export async function listInvites(workspaceId: string): Promise<Invite[]> {
  return pb.collection("invites").getFullList<Invite>({
    filter: pb.filter("workspace = {:workspaceId} && acceptedAt = ''", { workspaceId }),
    sort: "-created",
  });
}

/** Invitations are valid for two weeks; the email is sent by a PocketBase hook. */
export const INVITE_DAYS = 14;

export function inviteToken(): string {
  return globalThis.crypto.randomUUID().replace(/-/g, "");
}

export async function createInvite(
  workspaceId: string,
  email: string,
  role: Exclude<MemberRole, "owner">,
  invitedBy: string,
): Promise<Invite> {
  const expiresAt = new Date(Date.now() + INVITE_DAYS * 86_400_000).toISOString();
  return pb.collection("invites").create({
    workspace: workspaceId,
    email: email.trim().toLowerCase(),
    role,
    token: inviteToken(),
    expiresAt,
    invitedBy,
  });
}

export async function revokeInvite(id: string): Promise<void> {
  await pb.collection("invites").delete(id);
}

/** Redeems an invitation token. The route runs with elevated rights (see the hooks file). */
export async function acceptInvite(
  token: string,
): Promise<{ workspace: string; workspaceName: string; role: MemberRole }> {
  return pb.send("/api/invites/accept", { method: "POST", body: { token } });
}

export type InvitePreview = {
  workspaceName: string;
  inviterName: string;
  role: MemberRole;
  email: string;
  state: "open" | "used" | "expired";
};

/** What an invitation link is for, readable before signing in (server/pb_hooks/bookkeeping.pb.js). */
export async function previewInvite(token: string): Promise<InvitePreview> {
  return pb.send(`/api/invites/preview/${encodeURIComponent(token)}`, { method: "GET" });
}

export function roleIn(memberships: Membership[], workspaceId: string): MemberRole | null {
  return memberships.find((membership) => membership.workspace === workspaceId)?.role ?? null;
}

export function canWrite(role: MemberRole | null | undefined): boolean {
  return role !== null && role !== undefined && WRITE_ROLES.includes(role);
}

export function canManage(role: MemberRole | null | undefined): boolean {
  return role === "owner";
}
